'use client'

import {
  Flex,
  Pagination as MantinePagination,
  PaginationProps as MantinePaginationProps,
  Text,
} from '@mantine/core'
import NextLink from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'

import { useIsResponsiveMobileView, useIsTinyScreen } from '@/hooks/responsive'

function useItemLinkProps(pageQueryKey: string) {
  const searchParams = useSearchParams()
  const queryObject = Object.fromEntries(searchParams.entries())
  const pathname = usePathname()

  function getItemLinkProps(page: number): Record<string, any> {
    const newQueryObject = { ...queryObject }

    // Not showing the default first page (?page=1) on url
    if (page === 1) {
      delete newQueryObject[pageQueryKey]
      return {
        component: NextLink,
        href: Object.keys(newQueryObject).length > 0 ? { query: newQueryObject } : pathname,
      }
    }

    newQueryObject[pageQueryKey] = page.toString()

    return {
      component: NextLink,
      href: { query: newQueryObject },
    }
  }

  return getItemLinkProps
}

interface Props extends Omit<MantinePaginationProps, 'total' | 'value'> {
  /**
   * Whether to render the pagination controls as links instead of buttons for SEO purpose
   * @default true
   */
  controlsAsLinks?: boolean
  totalRecords: number
  recordsPerPage: number
  page: number
  /**
   * The query key to use for the page parameter.
   * @default 'page'
   */
  pageQueryKey?: string
  isOGPrice?: boolean
}

export default function Pagination({
  controlsAsLinks = true,
  totalRecords,
  recordsPerPage,
  page,
  pageQueryKey = 'page',
  isOGPrice = false,
  ...restProps
}: Props) {
  const t = useTranslations('coinTable')
  const totalPages = Math.ceil(totalRecords / recordsPerPage)

  const getItemLinkProps = useItemLinkProps(pageQueryKey)

  const isMobileView = useIsResponsiveMobileView()
  const isTinyScreen = useIsTinyScreen(389)
  const compact = isOGPrice && isTinyScreen

  const mantinePaginationProps: MantinePaginationProps = {
    total: totalPages,
    value: page,
    // Do not show sibling pages on mobile view to avoid wrapping lines

    ...(compact
      ? {
          siblings: 0,
          boundaries: 0,
        }
      : {
          // keep your current behavior
          siblings: isMobileView ? 0 : undefined,
        }),
    ...(controlsAsLinks
      ? {
          getItemProps: _page => getItemLinkProps(_page),
          getControlProps: control => {
            if (control === 'first') {
              return getItemLinkProps(1)
            }

            if (control === 'last') {
              return getItemLinkProps(totalPages)
            }

            if (control === 'next' && page < totalPages) {
              return getItemLinkProps(page + 1)
            }

            if (control === 'previous' && page > 1) {
              return getItemLinkProps(page - 1)
            }

            return {}
          },
        }
      : {}),
  }
  return (
    <Flex
      justify="space-between"
      align="center"
      direction={{ base: 'column', medium: 'row' }}
      rowGap="8px"
    >
      <Text variant="body" c="var(--content-secondary)">
        {t('pagination', {
          start: (page - 1) * recordsPerPage + 1,
          end: Math.min(page * recordsPerPage, totalRecords),
          total: totalRecords,
        })}
      </Text>
      <MantinePagination
        key={compact ? 'compact' : 'full'}
        {...mantinePaginationProps}
        {...restProps}
      />
    </Flex>
  )
}
